import Cookies from 'js-cookie';
import { getAuthenticatedUrl, getFormUrl, getLogoutUrl, getProxyUrl, getUserUrl, } from './AuthProxyUrlBuilder';
import { CachedValue } from './CachedValue';
export class AuthServiceV4 {
    constructor(fag, sharedEventEmitter, authServiceOptions, loginAllCallback, authServiceState, logger) {
        this.fag = fag;
        this.sharedEventEmitter = sharedEventEmitter;
        this.authServiceOptions = authServiceOptions;
        this.loginAllCallback = loginAllCallback;
        this.authServiceState = authServiceState;
        this.logger = logger;
        this.eventTimeout = 500;
        this.cachedAuthenticatedState = new CachedValue(60 * 1000); // 1 minute ttl
        this.cachedUserInfoState = new CachedValue(60 * 1000); // 1 minute ttl
    }
    async isAuthenticated() {
        return this.authServiceState.mutex.use(async () => {
            var _a;
            // We dont really support authentication during ssr, so we are never logged in
            if (this.isSsr()) {
                return false;
            }
            const cachedAuthenticatedState = this.cachedAuthenticatedState.get();
            if (cachedAuthenticatedState !== undefined) {
                return cachedAuthenticatedState;
            }
            let isAuthenticated = false;
            try {
                const authenticatedResponse = await this.fetchResource(getAuthenticatedUrl(this.authServiceOptions, this.fag));
                if (authenticatedResponse.status === 200) {
                    isAuthenticated = true;
                }
            }
            catch (e) {
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error('Requesting authentication status from authproxy has failed', e);
            }
            this.cachedAuthenticatedState.set(isAuthenticated);
            return isAuthenticated;
        });
    }
    async login(prompt, redirectUrl) {
        await this.sharedEventEmitter.emit('saveState');
        this.loginAllCallback(prompt, redirectUrl);
    }
    async logout(redirectUrl) {
        var _a, _b;
        await this.sharedEventEmitter.emit('logout');
        const csrfToken = (_a = Cookies.get('csrf_token')) !== null && _a !== void 0 ? _a : '';
        const logoutRedirectUrl = redirectUrl ||
            (typeof this.authServiceOptions.defaultLogoutRedirectUrl === 'function'
                ? this.authServiceOptions.defaultLogoutRedirectUrl()
                : this.authServiceOptions.defaultLogoutRedirectUrl);
        if (!csrfToken) {
            (_b = this.logger) === null || _b === void 0 ? void 0 : _b.error("We don't have a csrf_token cookie, so the logout will fail as a csrf parameter is required.");
            return;
        }
        this.cachedAuthenticatedState.clear();
        this.cachedUserInfoState.clear();
        window.location.assign(getLogoutUrl(this.authServiceOptions, window.location.origin, csrfToken, logoutRedirectUrl));
    }
    subscribe(event, handlerFunction) {
        this.sharedEventEmitter.on(event, async () => {
            // eslint-disable-next-line no-promise-executor-return
            const timeoutPromise = new Promise((resolve) => setTimeout(resolve, this.eventTimeout));
            return Promise.race([timeoutPromise, handlerFunction()]);
        });
    }
    async fetch(uri, init) {
        var _a;
        if (!(init === null || init === void 0 ? void 0 : init.resourceHostId)) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.warn(`The request to ${uri} has no resourceHost defined`);
        }
        // Stop the risk to trigger a race condition and create multiple conflicting authproxy sessions.
        if (!(await this.isAuthenticated())) {
            return new Response(undefined, { status: 403 });
        }
        return this.fetchResource(getProxyUrl(this.authServiceOptions, this.fag, init === null || init === void 0 ? void 0 : init.resourceHostId, uri), init);
    }
    getFormBaseUrl(path) {
        return getFormUrl(this.authServiceOptions, path !== null && path !== void 0 ? path : '');
    }
    async getUserInfo() {
        if (!this.authServiceState.userInfoMutex) {
            // That should never happen, but we needed the typings to be flexible to continue v2 support
            return undefined;
        }
        return this.authServiceState.userInfoMutex.use(async () => {
            var _a, _b, _c, _d;
            const cachedUserInfoState = this.cachedUserInfoState.get();
            if (cachedUserInfoState !== undefined) {
                return cachedUserInfoState;
            }
            if (!(await this.isAuthenticated())) {
                (_a = this.logger) === null || _a === void 0 ? void 0 : _a.warn('Requesting userinfo while not authenticated');
                return undefined;
            }
            try {
                const userInfoResponse = await this.fetchResource(getUserUrl(this.authServiceOptions, this.fag));
                if (userInfoResponse.status === 403) {
                    (_b = this.logger) === null || _b === void 0 ? void 0 : _b.error('Requesting userinfo while not authenticated, something went really wrong as this should not happen');
                    this.cachedUserInfoState.clear();
                }
                if (userInfoResponse.status === 200) {
                    const userInfo = await userInfoResponse.json();
                    this.cachedUserInfoState.set(userInfo);
                    return userInfo;
                }
                (_c = this.logger) === null || _c === void 0 ? void 0 : _c.error('Requesting userinfo ended in a unexpected state');
            }
            catch (e) {
                (_d = this.logger) === null || _d === void 0 ? void 0 : _d.error('Requesting authentication status from authproxy has failed', e);
            }
            return undefined;
        });
    }
    fetchResource(path, init) {
        var _a;
        const csrfToken = Cookies.get('csrf_token') || '';
        if (!csrfToken) {
            (_a = this.logger) === null || _a === void 0 ? void 0 : _a.error("We don't have a csrf_token cookie, so the fetch would fail.");
        }
        return fetch(path, Object.assign(Object.assign({}, init), { 
            // eslint-disable-next-line @typescript-eslint/naming-convention
            headers: Object.assign(Object.assign({}, init === null || init === void 0 ? void 0 : init.headers), { 'x-csrf-token': csrfToken }), credentials: 'include' }));
    }
    isSsr() {
        return typeof window === 'undefined';
    }
}
//# sourceMappingURL=AuthServiceV4.js.map